import React, { useState } from 'react';
import { Box, Typography, Grid, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Link, Container, Button, Chip } from '@mui/material';
import { LineChart } from '@mui/x-charts/LineChart';
import NextLink from 'next/link';
import useAuth from '../../hook/auth';
import { chart as chartTokens, card as cardTokens, performance as perfTokens } from '../../utils/designTokens';

const getSeriesColor = (modelName) => chartTokens.series[modelName] || chartTokens.series.LARGECAP;

const MetricBox = ({ label, value, sub, positive }) => (
  <Box sx={{ textAlign: 'center', flex: 1, minWidth: 0 }}>
    <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', fontSize: '0.7rem', fontWeight: 500, mb: 0.25 }}>
      {label}
    </Typography>
    <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.95rem', color: 'text.primary', letterSpacing: '-0.01em' }}>
      {value}
    </Typography>
    {/* <br /> */}
    {sub && (
      <Typography variant="caption" sx={{
        fontSize: '0.7rem', fontWeight: 600, display: 'block',
        color: positive ? perfTokens.positive.text : positive === false ? perfTokens.negative.text : 'text.secondary',
      }}>
        {sub}
      </Typography>
    )}
  </Box>
);

const ModelCard = ({ modelName, data, isLoggedIn }) => {
  const [showAllTickers, setShowAllTickers] = useState(false);
  const [hoverIndex, setHoverIndex] = useState(null);
  const chartRef = React.useRef(null);
  const CHART_MARGIN_LEFT = 60;
  const CHART_MARGIN_RIGHT = 10;

  const seriesColor = getSeriesColor(modelName);
  const benchmarkName = modelName === 'MIDCAP' ? 'S&P Mid-Cap ETF'
    : modelName === 'SMALLCAP' ? 'S&P 600'
      : modelName === 'QQQ' ? 'QQQ'
        : modelName === 'SPY' ? 'SPY'
          : 'S&P 500';

  const isFundModel = modelName === 'QQQ' || modelName === 'SPY';

  const titleLabel = isFundModel
    ? `Grufity Fund Selection vs ${benchmarkName}`
    : `Grufity High Scoring ${modelName === 'LARGECAP' ? 'Large Caps' : modelName === 'MIDCAP' ? 'Mid Caps' : 'Small Caps'} vs ${benchmarkName}`;

  const descLabel = isFundModel
    ? `Performance of our hedge fund selection strategy vs ${benchmarkName} ETF benchmark.`
    : 'Our model selects high-potential stocks competing directly with the index.';

  const perfTrail = data['PERF.TRAIL'] || [];
  const perfChart = data['PERFCHART'] || [];
  const allTickers = isLoggedIn ? (data['TICKERS'] || []) : (data['SAMPLETICKS'] || []);
  const quarters = data['PERF|Q'] || data['PERF|Y'] || [];

  const formatPercent = (v) => {
    if (v === undefined || v === null || v === '') return '0';
    const num = parseFloat(v);
    return Number.isNaN(num) ? v : num.toFixed(2);
  };

  const formatCurrency = (v) => {
    if (v === undefined || v === null || v === '') return '$0';
    const num = parseFloat(v);
    return Number.isNaN(num) ? v : `$${Math.round(num).toLocaleString()}`;
  };

  const synthesizeTrail = (chart) => {
    if (!chart || chart.length === 0) return [];
    const periods = [30, 90, 182, 365, 365 * 3];
    const nowPoint = chart[chart.length - 1];
    const nowTs = new Date(nowPoint.date).getTime();

    const findClosestBefore = (targetTs) => {
      for (let i = chart.length - 1; i >= 0; i--) {
        if (new Date(chart[i].date).getTime() <= targetTs) return i;
      }
      return 0;
    };

    return periods.map((days, idx) => {
      const i = findClosestBefore(nowTs - days * 86400000);
      const p = chart[i];
      const grStart = p.SLC || p.SMC || p.grufity || 0;
      const bfStart = p.SPY || p.IJH || p.nifty || 0;
      const grNow = nowPoint.SLC || nowPoint.SMC || nowPoint.grufity || 0;
      const bfNow = nowPoint.SPY || nowPoint.IJH || nowPoint.nifty || 0;
      const grPct = grStart ? ((grNow / grStart) - 1) * 100 : 0;
      const bfPct = bfStart ? ((bfNow / bfStart) - 1) * 100 : 0;
      const alpha = grPct - bfPct;
      const labels = ['Past Month', 'Past Quarter', 'Past 6 Months', 'Past Year', 'Past 3 Years'];

      return {
        Period: labels[idx],
        QQQ: formatPercent(bfPct), SPY: formatPercent(bfPct),
        SMC: formatPercent(grPct), SLC: formatPercent(grPct),
        stmt: alpha >= 0 ? `Beats by ${alpha.toFixed(2)}%` : `Lags by ${Math.abs(alpha).toFixed(2)}%`,
      };
    }).filter(Boolean);
  };

  let winningData = perfTrail.length > 0 ? perfTrail
    : perfChart.length > 0 ? synthesizeTrail(perfChart)
      : quarters.slice(0, 5);

  if (Array.isArray(winningData) && winningData.length > 0 && winningData[0].Period) {
    winningData = winningData.filter(item => ['Past Quarter', 'Past Year', 'Past 3 Years'].includes(item.Period));
  }

  const formatYearLabel = (item) => {
    if (!item) return '';
    if (item.date) { try { return new Date(item.date).getFullYear().toString(); } catch (e) { /* */ } }
    if (item.year) return item.year.toString();
    if (item.Period) return item.Period.toString();
    return '';
  };

  const displayTickers = showAllTickers ? allTickers : allTickers.slice(0, 5);

  const chartData = perfChart.map(d => ({
    date: d.date,
    grufity: d.Grufity || d.SLC || d.SMC || 0,
    nifty: d.QQQ || d.SPY || d.IJH || 0,
    SLC: d.SLC, SMC: d.SMC, SPY: d.SPY, IJH: d.IJH, Grufity: d.Grufity, QQQ: d.QQQ,
  }));

  const lastPoint = perfChart[perfChart.length - 1] || {};
  const grufityValue = formatCurrency(lastPoint.Grufity || lastPoint.SLC || lastPoint.SMC);
  const niftyValue = formatCurrency(lastPoint.QQQ || lastPoint.SPY || lastPoint.IJH);

  React.useEffect(() => {
    if (chartData.length > 0) setHoverIndex(chartData.length - 1);
  }, [chartData.length]);

  const handleChartMouseMove = (e) => {
    const el = chartRef.current;
    if (!el || chartData.length === 0) return;
    const rect = el.getBoundingClientRect();
    const drawingWidth = rect.width - CHART_MARGIN_LEFT - CHART_MARGIN_RIGHT;
    const x = Math.max(0, Math.min(e.clientX - rect.left - CHART_MARGIN_LEFT, drawingWidth));
    const ratio = x / drawingWidth;
    const PAD = 45 * 86400000;
    const domainMin = new Date(chartData[0].date).getTime() - PAD;
    const domainMax = new Date(chartData[chartData.length - 1].date).getTime() + PAD;
    const targetTs = domainMin + ratio * (domainMax - domainMin);
    let closestIdx = 0;
    let minDiff = Infinity;
    chartData.forEach((d, i) => {
      const diff = Math.abs(new Date(d.date).getTime() - targetTs);
      if (diff < minDiff) { minDiff = diff; closestIdx = i; }
    });
    setHoverIndex(closestIdx);
  };

  const handleChartMouseLeave = () => setHoverIndex(chartData.length - 1);

  const gradientId = `grad_${modelName}`;

  return (
    <Grid key={modelName} size={{ md: 6, xs: 12 }}>
      <Paper
        elevation={0}
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          bgcolor: cardTokens.bg,
          border: '1px solid',
          borderColor: cardTokens.premium.border,
          borderRadius: 2,
          boxShadow: cardTokens.premium.shadow,
          transition: 'box-shadow 300ms ease, border-color 300ms ease',
          overflow: 'hidden',
          '&:hover': {
            boxShadow: cardTokens.premium.shadowHover,
            borderColor: cardTokens.borderHover,
          },
        }}
      >
        {/* <Box sx={{ height: 3, bgcolor: seriesColor.main }} /> */}

        <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
          {/* p: { xs: 2.5, md: 3 }, */}
          <Box sx={{
            mb: 2.5,
            borderBottom: '1px solid',
            borderColor: 'grey.200',
            // borderRadius: 2,
            bgcolor: '#fafbfc',
            overflow: 'hidden',
          }}>

            <Box sx={{ my: 1.5, mx: 2.5 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '0.95rem', color: 'text.primary', mb: 0.5 }}>
                {titleLabel}
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.78rem', lineHeight: 1.4 }}>
                {descLabel}
              </Typography>
            </Box>
            <Box sx={{
              px: 2.5, pt: 0, pb: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: 1,
            }}>
              <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.82rem', color: 'text.primary' }}>
                Growth of $100,000
              </Typography>
              <Box sx={{ display: 'flex', gap: 2.5, alignItems: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: seriesColor.main }} />
                  <Typography variant="caption" sx={{ fontSize: '0.72rem', fontWeight: 600, color: seriesColor.main }}>
                    Grufity {grufityValue}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: chartTokens.benchmark.main }} />
                  <Typography variant="caption" sx={{ fontSize: '0.72rem', fontWeight: 500, color: chartTokens.benchmark.main }}>
                    {benchmarkName} {niftyValue}
                  </Typography>
                </Box>
              </Box>
            </Box>

            {hoverIndex !== null && chartData[hoverIndex] && (
              <Box sx={{ px: 2.5, pb: 0.5, display: 'flex', alignItems: 'baseline', gap: 2, minHeight: 20 }}>
                <Typography variant="caption" sx={{ fontSize: '0.68rem', color: 'text.secondary', fontWeight: 500 }}>
                  {new Date(chartData[hoverIndex].date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                </Typography>
                <Typography variant="caption" sx={{ fontSize: '0.72rem', fontWeight: 700, color: seriesColor.main }}>
                  {formatCurrency(chartData[hoverIndex].grufity)}
                </Typography>
                <Typography variant="caption" sx={{ fontSize: '0.72rem', fontWeight: 500, color: chartTokens.benchmark.main }}>
                  {formatCurrency(chartData[hoverIndex].nifty)}
                </Typography>
              </Box>
            )}

            {chartData.length > 0 ? (
              <Box
                sx={{ width: '100%', height: 320, position: 'relative', cursor: 'crosshair' }}
                ref={chartRef}
                onMouseMove={handleChartMouseMove}
                onMouseLeave={handleChartMouseLeave}
              >
                <LineChart
                  dataset={chartData.map(d => ({ ...d, date: new Date(d.date).getTime() }))}
                  xAxis={[{
                    dataKey: 'date',
                    scaleType: 'time',
                    min: chartData.length > 0 ? new Date(chartData[0].date).getTime() - 45 * 86400000 : undefined,
                    max: chartData.length > 0 ? new Date(chartData[chartData.length - 1].date).getTime() + 45 * 86400000 : undefined,
                    valueFormatter: (date) => new Date(date).getFullYear().toString(),
                    tickNumber: 10,
                    tickLabelStyle: { fontSize: 11, fill: chartTokens.axisLabel, fontFamily: 'inherit', fontWeight: 500 },
                  }]}
                  yAxis={[{
                    scaleType: 'linear',
                    min: chartData.length > 0
                      ? Math.min(...chartData.map(d => Math.min(d.grufity || Infinity, d.nifty || Infinity))) * 0.94
                      : undefined,
                    max: chartData.length > 0
                      ? Math.max(...chartData.map(d => Math.max(d.grufity || 0, d.nifty || 0))) * 1.04
                      : undefined,
                    valueFormatter: (value) => `$${(value / 1000).toFixed(0)}k`,
                    tickLabelStyle: { fontSize: 11, fill: chartTokens.axisLabel, fontFamily: 'inherit', fontWeight: 500 },
                    tickNumber: 7,
                  }]}
                  series={[
                    {
                      dataKey: 'grufity',
                      label: 'Grufity',
                      color: seriesColor.main,
                      showMark: false,
                      curve: 'natural',
                      area: true,
                      baseline: 'min',
                      stackStrategy: 'none',
                    },
                    {
                      dataKey: 'nifty',
                      label: benchmarkName,
                      color: chartTokens.benchmark.main,
                      showMark: false,
                      curve: 'natural',
                    },
                  ]}
                  height={320}
                  margin={{ left: 17, right: 25, top: 5, bottom: 20 }}
                  grid={{ vertical: false, horizontal: true }}
                  slotProps={{ legend: { hidden: true }, tooltip: { trigger: 'none' } }}
                  sx={{
                    '.MuiLineElement-root': {
                      strokeWidth: 2.5,
                      transition: 'all 0.3s ease',
                    },
                    '.MuiAreaElement-root': {
                      fill: `url(#${gradientId})`,
                      fillOpacity: 1,
                    },
                    '.MuiChartsGrid-line': {
                      stroke: chartTokens.grid,
                      strokeDasharray: '3 3',
                      strokeWidth: 1,
                    },
                    '.MuiChartsAxis-line': { stroke: 'transparent !important' },
                    '.MuiChartsAxis-tick': { stroke: 'transparent !important' },
                    '.MuiChartsLegend-root': { display: 'none !important' },
                  }}
                >
                  <defs>
                    <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={seriesColor.main} stopOpacity={chartTokens.area.topOpacity} />
                      <stop offset="50%" stopColor={seriesColor.light} stopOpacity={chartTokens.area.midOpacity} />
                      <stop offset="100%" stopColor={seriesColor.light} stopOpacity={chartTokens.area.bottomOpacity} />
                    </linearGradient>
                  </defs>
                </LineChart>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 320 }}>
                <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.82rem' }}>
                  Chart data is currently unavailable
                </Typography>
              </Box>
            )}
          </Box>

          {chartData.length > 0 && (() => {
            const g = chartData[chartData.length - 1]?.grufity || 0;
            const n = chartData[chartData.length - 1]?.nifty || 0;
            const gReturn = g > 0 ? (((g / 100000) - 1) * 100) : 0;
            const nReturn = n > 0 ? (((n / 100000) - 1) * 100) : 0;
            const alpha = gReturn - nReturn;
            return (
              <Box sx={{
                display: 'flex',
                mb: 2.5,
                mx: 2,
                p: 1.5,
                borderRadius: 1.5,
                bgcolor: alpha >= 0 ? perfTokens.positive.bg : perfTokens.negative.bg,
                border: '1px solid',
                borderColor: alpha >= 0 ? perfTokens.positive.border : perfTokens.negative.border,
                gap: 0,
              }}>
                <MetricBox label="Grufity" value={formatCurrency(g)} sub={`${gReturn >= 0 ? '+' : ''}${gReturn.toFixed(1)}%`} positive={gReturn >= 0} />
                <Box sx={{ width: '1px', bgcolor: alpha >= 0 ? perfTokens.positive.border : perfTokens.negative.border, mx: 1 }} />
                <MetricBox label={benchmarkName} value={formatCurrency(n)} sub={`${nReturn >= 0 ? '+' : ''}${nReturn.toFixed(1)}%`} positive={nReturn >= 0} />
                <Box sx={{ width: '1px', bgcolor: alpha >= 0 ? perfTokens.positive.border : perfTokens.negative.border, mx: 1 }} />
                <MetricBox
                  label="Alpha"
                  value={`${alpha >= 0 ? '+' : ''}${alpha.toFixed(2)}%`}
                  sub={alpha >= 0 ? 'Outperforming' : 'Underperforming'}
                  positive={alpha >= 0}
                />
              </Box>
            );
          })()}

          <Box sx={{ display: 'flex', gap: 2, mb: 2.5, mx: 2, flexDirection: { xs: 'column', sm: 'column' } }}>

            {!isFundModel && winningData.length > 0 && (
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="caption" sx={{ fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.secondary', display: 'block', mb: 1 }}>
                  Who is Winning?
                </Typography>
                <TableContainer sx={{ border: '1px solid', borderColor: 'grey.200', borderRadius: 1.5, overflow: 'hidden' }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: 'grey.50', '& .MuiTableCell-head': { whiteSpace: 'nowrap !important' } }}>
                        <TableCell sx={{ fontWeight: 700, fontSize: '0.65rem', textTransform: 'uppercase', color: 'text.secondary', borderBottom: '1px solid', borderColor: 'grey.200', py: 0.75 }}>Period</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.65rem', textTransform: 'uppercase', color: 'text.secondary', borderBottom: '1px solid', borderColor: 'grey.200', py: 0.75 }}>Model</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.65rem', textTransform: 'uppercase', color: 'text.secondary', borderBottom: '1px solid', borderColor: 'grey.200', py: 0.75 }}>{benchmarkName}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.65rem', textTransform: 'uppercase', color: 'text.secondary', borderBottom: '1px solid', borderColor: 'grey.200', py: 0.75 }}>Result</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {winningData.map((item, index) => {
                        const modelKey = 'SharesGuru.in_' + modelName;
                        const modelReturn = item.Grufity || item[modelKey] || item.SLC || item.SMC || '0';
                        const benchmarkReturn = item.QQQ || item.SPY || item.IJH || '0';
                        const alpha = item.alpha !== undefined ? item.alpha : (parseFloat(modelReturn) - parseFloat(benchmarkReturn));
                        const positive = alpha > 0;

                        return (
                          <TableRow key={index} sx={{ '&:last-child td': { borderBottom: 0 }, }}>
                            <TableCell sx={{ fontSize: '0.72rem', fontWeight: 600, color: 'text.primary', py: 0.85, borderBottom: '1px solid', borderColor: 'grey.100', }}>
                              {item.Period || item.year}
                            </TableCell>
                            <TableCell align="right" sx={{ fontSize: '0.72rem', fontWeight: 700, color: parseFloat(modelReturn) >= 0 ? perfTokens.positive.text : perfTokens.negative.text, py: 0.85, borderBottom: '1px solid', borderColor: 'grey.100' }}>
                              {modelReturn}%
                            </TableCell>
                            <TableCell align="right" sx={{ fontSize: '0.72rem', color: 'text.secondary', py: 0.85, borderBottom: '1px solid', borderColor: 'grey.100' }}>
                              {benchmarkReturn}%
                            </TableCell>
                            <TableCell align="right" sx={{ py: 0.85, borderBottom: '1px solid', borderColor: 'grey.100' }}>
                              <Chip
                                label={positive ? `+${alpha.toFixed(1)}%` : `${alpha.toFixed(1)}%`}
                                size="small"
                                sx={{
                                  height: 20,
                                  fontSize: '0.65rem',
                                  fontWeight: 700,
                                  bgcolor: positive ? perfTokens.positive.bg : perfTokens.negative.bg,
                                  color: positive ? perfTokens.positive.text : perfTokens.negative.text,
                                  border: '1px solid',
                                  borderColor: positive ? perfTokens.positive.border : perfTokens.negative.border,
                                  '& .MuiChip-label': { px: 0.75 },
                                }}
                              />
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}

            {allTickers && allTickers.length > 0 && (
              <Box sx={{ width: { xs: '100%', sm: '100%' }, flexShrink: 0, position: 'relative' }}>
                <Typography variant="caption" sx={{ fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.secondary', display: 'block', mb: 1 }}>
                  Portfolio
                </Typography>
                <Box sx={{ position: 'relative' }}>
                  <TableContainer sx={{
                    border: '1px solid',
                    borderColor: !isLoggedIn ? 'grey.300' : 'grey.200',
                    borderRadius: 1.5,
                    maxHeight: isLoggedIn && showAllTickers ? 220 : 'auto',
                    overflowY: isLoggedIn && showAllTickers ? 'auto' : 'visible',
                    filter: !isLoggedIn ? 'blur(5px)' : 'none',
                    pointerEvents: !isLoggedIn ? 'none' : 'auto',
                    transition: 'filter 0.3s ease',
                    overflow: 'auto',
                  }}>
                    {/* <Box sx={{ height: 2, bgcolor: seriesColor.main }} /> */}
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: 'grey.50' }}>
                          <TableCell sx={{ fontWeight: 700, fontSize: '0.65rem', textTransform: 'uppercase', color: 'text.secondary', borderBottom: '1px solid', borderColor: 'grey.200', py: 0.75, textAlign: 'center' }}>Ticker</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.65rem', textTransform: 'uppercase', color: 'text.secondary', borderBottom: '1px solid', borderColor: 'grey.200', py: 0.75, textAlign: 'center' }}>Weight</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {displayTickers.map((ticker, idx) => {
                          const tickerSymbol = ticker.ticker || ticker;
                          const isHidden = !isLoggedIn && tickerSymbol.includes('*');
                          return (
                            <TableRow key={idx} sx={{ '&:hover': { bgcolor: 'grey.50' } }}>
                              <TableCell sx={{ fontSize: '0.75rem', borderBottom: '1px solid', borderColor: 'grey.100', py: 0.75, textAlign: 'center' }}>
                                {isHidden ? (
                                  <Typography variant="body2" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>{tickerSymbol}</Typography>
                                ) : (
                                  <NextLink href={`/stock/${tickerSymbol}`} passHref legacyBehavior>
                                    <Link sx={{ textDecoration: 'none', color: seriesColor.main, fontSize: '0.75rem', fontWeight: 600 }}>
                                      {tickerSymbol}
                                    </Link>
                                  </NextLink>
                                )}
                              </TableCell>
                              <TableCell align="right" sx={{ fontSize: '0.72rem', borderBottom: '1px solid', borderColor: 'grey.100', py: 0.75, color: 'text.secondary', textAlign: 'center' }}>
                                {ticker.percent || '\u2014'}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                        {isLoggedIn && allTickers.length > 5 && (
                          <TableRow>
                            <TableCell colSpan={2} sx={{ textAlign: 'center', py: 0.5, borderBottom: 'none', bgcolor: 'grey.50' }}>
                              <Link
                                component="button"
                                onClick={() => setShowAllTickers(!showAllTickers)}
                                sx={{ fontSize: '0.72rem', textDecoration: 'none', cursor: 'pointer', fontWeight: 600, color: 'primary.main' }}
                              >
                                {showAllTickers ? 'Show Less' : `Show all ${allTickers.length}`}
                              </Link>
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  {!isLoggedIn && (
                    <Box sx={{
                      position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2,
                    }}>
                      <NextLink href="/pricing" passHref legacyBehavior>
                        <Button
                          variant="contained"
                          sx={{
                            fontSize: '0.78rem', fontWeight: 600, px: 2.5, py: 0.75,
                            borderRadius: 1.5, textTransform: 'none',
                            bgcolor: 'primary.main', color: '#fff',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
                            '&:hover': { bgcolor: 'primary.dark' },
                          }}
                        >
                          Subscribe to View
                        </Button>
                      </NextLink>
                    </Box>
                  )}
                </Box>
              </Box>
            )}
          </Box>

          {quarters && quarters.length > 0 && (
            <Box sx={{ mt: 'auto', mx: 2, mb: 2 }}>
              <Typography variant="caption" sx={{ fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.secondary', display: 'block', mb: 1 }}>
                Yearly Performance
              </Typography>
              <TableContainer sx={{ border: '1px solid', borderColor: 'grey.200', borderRadius: 1.5, overflow: 'auto' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'grey.50' }}>
                      <TableCell sx={{ fontWeight: 700, fontSize: '0.65rem', textTransform: 'uppercase', color: 'text.secondary', borderBottom: '1px solid', borderColor: 'grey.200', py: 0.75 }}>Year</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.65rem', textTransform: 'uppercase', color: 'text.secondary', borderBottom: '1px solid', borderColor: 'grey.200', py: 0.75, textWrap: 'nowrap' }}>
                        {isFundModel ? 'Grufity Funds' : `High Scoring ${modelName === 'LARGECAP' ? 'Large' : modelName === 'MIDCAP' ? 'Mid' : 'Small'} Caps`}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.65rem', textTransform: 'uppercase', color: 'text.secondary', borderBottom: '1px solid', borderColor: 'grey.200', py: 0.75 }}>
                        {benchmarkName}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.65rem', textTransform: 'uppercase', color: 'text.secondary', borderBottom: '1px solid', borderColor: 'grey.200', py: 0.75 }}>Result</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {quarters.slice(0, 10).map((quarter, idx) => {
                      const modelReturn = quarter.Grufity || quarter.SLC || quarter.SMC || '0';
                      const benchmarkReturn = quarter.QQQ || quarter.SPY || quarter.IJH || '0';
                      const modelNum = parseFloat(modelReturn);
                      const benchmarkNum = parseFloat(benchmarkReturn);
                      const alpha = modelNum - benchmarkNum;
                      const isLast = idx === Math.min(quarters.length, 10) - 1;

                      return (
                        <TableRow key={idx} sx={{ '&:nth-of-type(even)': { bgcolor: 'grey.50' }, }}>
                          <TableCell sx={{ fontSize: '0.72rem', fontWeight: 500, borderBottom: isLast ? 'none' : '1px solid', borderColor: 'grey.100', py: 0.85, }}>
                            {formatYearLabel(quarter)}
                          </TableCell>
                          <TableCell align="right" sx={{
                            fontSize: '0.72rem', fontWeight: 700,
                            color: modelNum >= 0 ? perfTokens.positive.text : perfTokens.negative.text,
                            borderBottom: isLast ? 'none' : '1px solid', borderColor: 'grey.100', py: 0.85,
                          }}>
                            {modelReturn}%
                          </TableCell>
                          <TableCell align="right" sx={{
                            fontSize: '0.72rem', fontWeight: 600,
                            color: benchmarkNum >= 0 ? perfTokens.positive.text : perfTokens.negative.text,
                            borderBottom: isLast ? 'none' : '1px solid', borderColor: 'grey.100', py: 0.85,
                          }}>
                            {benchmarkReturn}%
                          </TableCell>
                          <TableCell align="right" sx={{ borderBottom: isLast ? 'none' : '1px solid', borderColor: 'grey.100', py: 0.85 }}>
                            <Chip
                              label={quarter.stmt || (alpha >= 0 ? `+${alpha.toFixed(1)}%` : `${alpha.toFixed(1)}%`)}
                              size="small"
                              sx={{
                                height: 20,
                                fontSize: '.75rem',
                                fontWeight: 700,
                                bgcolor: alpha >= 0 ? perfTokens.positive.bg : perfTokens.negative.bg,
                                color: alpha >= 0 ? perfTokens.positive.text : perfTokens.negative.text,
                                border: '1px solid',
                                borderColor: alpha >= 0 ? perfTokens.positive.border : perfTokens.negative.border,
                                '& .MuiChip-label': { px: 0.75 },
                              }}
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </Box>
      </Paper>
    </Grid>
  );
};

const GrufityModel = ({ modelData }) => {
  const { user } = useAuth();
  const isLoggedIn = !!user;
  const [showFullDescription, setShowFullDescription] = useState(false);

  if (!modelData || modelData.length === 0) return null;

  const models = {};
  modelData.forEach(item => {
    const parts = item.seriesCode.split('.');
    if (parts.length >= 2) {
      const modelName = parts[0].replace('SHG_', '').replace('GRUF_', '');
      if (!models[modelName]) models[modelName] = {};
      const type = parts.slice(1).join('.');

      try {
        if (item.v && typeof item.v === 'string') {
          try {
            models[modelName][type] = JSON.parse(item.v);
          } catch (parseError) {
            if (type === 'PERFCHART') {
              let fixedValue = item.v.trim();
              const lastComplete = fixedValue.lastIndexOf('},{');
              if (lastComplete > 0) {
                fixedValue = fixedValue.substring(0, lastComplete + 1) + ']';
                try { models[modelName][type] = JSON.parse(fixedValue); }
                catch (e2) { models[modelName][type] = []; }
              } else {
                models[modelName][type] = [];
              }
            } else {
              throw parseError;
            }
          }
        } else if (Array.isArray(item.v)) {
          models[modelName][type] = item.v;
        } else if (item.v) {
          models[modelName][type] = item.v;
        }
      } catch (e) {
        console.error('Check API/database.');
      }
    }
  });

  const modelOrder = ['QQQ', 'SPY', 'LARGECAP', 'MIDCAP'];
  const sortedModelNames = Object.keys(models)
    .filter(name => models[name].PERFCHART && models[name].PERFCHART.length > 0)
    .sort((a, b) => {
      const aIdx = modelOrder.indexOf(a);
      const bIdx = modelOrder.indexOf(b);
      if (aIdx === -1 && bIdx === -1) return 0;
      if (aIdx === -1) return 1;
      if (bIdx === -1) return -1;
      return aIdx - bIdx;
    });

  const isFundModel = sortedModelNames.some(name => name === 'QQQ' || name === 'SPY');
  const titleText = isFundModel ? 'Grufity Hedge Fund Selection Performance' : 'Grufity Portfolio Performance vs S&P 500';

  const descriptionShort = isFundModel
    ? 'We track hedge funds selected by our model against major ETF benchmarks.'
    : 'A live, real-time test of the Grufity model \u2014 rebalanced every two weeks against the S&P 500.';
  const descriptionFull = isFundModel
    ? 'We track the real-time performance of hedge funds selected by our proprietary model against major market ETF benchmarks (QQQ and SPY). Our algorithm analyzes hedge fund holdings and performance metrics to identify funds with high-growth potential. This transparent tracking allows investors to evaluate our fund selection methodology and compare returns against passive index investing strategies.'
    : 'We are conducting a live, real-time test of the Grufity in-house model. Every two weeks, our algorithm identifies high-potential stocks and allocates money equally to them. We track its performance live against the S&P 500 index. While the model has outperformed in backtests, live markets are the true test. Follow its performance here.';

  return (
    <Box sx={{ py: { xs: 4, md: 6 } }}>
      <Box sx={{ mb: 5, textAlign: 'center', maxWidth: 720, mx: 'auto' }}>
        <Typography variant="overline" component="div" sx={{ mb: 1 }}>
          Live Performance
        </Typography>
        <Typography variant="h2" component="h2" sx={{ mb: 1.5 }}>
          {titleText}
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.7, maxWidth: 640, mx: 'auto' }}>
          {showFullDescription ? descriptionFull : descriptionShort}
          <Link
            component="button"
            onClick={() => setShowFullDescription(!showFullDescription)}
            sx={{ color: 'primary.main', fontSize: 'inherit', textDecoration: 'none', fontWeight: 600, cursor: 'pointer', ml: 0.5 }}
          >
            {showFullDescription ? 'Less' : 'More'}
          </Link>
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {sortedModelNames.map(modelName => (
          <ModelCard key={modelName} modelName={modelName} data={models[modelName]} isLoggedIn={isLoggedIn} />
        ))}
      </Grid>
    </Box>
  );
};

export default GrufityModel;
